import React, { useState, useRef, useEffect } from 'react';

export const ImageCropModal = ({ image, onClose, onCrop, aspectRatio = null }) => {
    const canvasRef = useRef(null);
    const [crop, setCrop] = useState({ x: 0, y: 0, width: 1, height: 1 });
    const [zoom, setZoom] = useState(1);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageObj, setImageObj] = useState(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            setImageObj(img);

            // Calculate initial crop based on aspect ratio
            if (aspectRatio) {
                const imgRatio = img.width / img.height;
                let width = 0.8;
                let height = 0.8;

                if (aspectRatio > imgRatio) {
                    // Wider than image
                    height = width / aspectRatio * imgRatio;
                } else {
                    // Taller than image
                    width = height * aspectRatio / imgRatio;
                }

                const x = (1 - width) / 2;
                const y = (1 - height) / 2;

                setCrop({ x, y, width, height });
            }
        };
        img.src = image;
    }, [image, aspectRatio]);

    useEffect(() => {
        if (!imageObj || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const container = canvas.parentElement;

        // Set container size
        const maxWidth = Math.min(container.clientWidth, 600);
        const scale = maxWidth / imageObj.width;
        const displayWidth = maxWidth;
        const displayHeight = imageObj.height * scale;

        setContainerSize({ width: displayWidth, height: displayHeight });

        canvas.width = displayWidth;
        canvas.height = displayHeight;

        // Clear canvas
        ctx.clearRect(0, 0, displayWidth, displayHeight);

        // Calculate zoomed dimensions
        const zoomedWidth = displayWidth * zoom;
        const zoomedHeight = displayHeight * zoom;

        // Calculate image position with zoom
        const imgX = imagePosition.x;
        const imgY = imagePosition.y;

        // Draw zoomed image
        ctx.drawImage(imageObj, imgX, imgY, zoomedWidth, zoomedHeight);

        // Draw crop overlay
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, displayWidth, displayHeight);

        // Clear crop area
        const cropX = crop.x * displayWidth;
        const cropY = crop.y * displayHeight;
        const cropWidth = crop.width * displayWidth;
        const cropHeight = crop.height * displayHeight;

        ctx.clearRect(cropX, cropY, cropWidth, cropHeight);

        // Draw image in crop area
        ctx.save();
        ctx.beginPath();
        ctx.rect(cropX, cropY, cropWidth, cropHeight);
        ctx.clip();
        ctx.drawImage(imageObj, imgX, imgY, zoomedWidth, zoomedHeight);
        ctx.restore();

        // Draw crop border
        ctx.strokeStyle = '#e2b77b';
        ctx.lineWidth = 3;
        ctx.strokeRect(cropX, cropY, cropWidth, cropHeight);

        // Draw corner handles
        const handleSize = 14;
        ctx.fillStyle = '#e2b77b';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 4;

        // Top-left
        ctx.fillRect(cropX - handleSize / 2, cropY - handleSize / 2, handleSize, handleSize);
        // Top-right
        ctx.fillRect(cropX + cropWidth - handleSize / 2, cropY - handleSize / 2, handleSize, handleSize);
        // Bottom-left
        ctx.fillRect(cropX - handleSize / 2, cropY + cropHeight - handleSize / 2, handleSize, handleSize);
        // Bottom-right
        ctx.fillRect(cropX + cropWidth - handleSize / 2, cropY + cropHeight - handleSize / 2, handleSize, handleSize);

        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;

    }, [imageObj, crop, zoom, imagePosition, containerSize]);

    const handleMouseDown = (e) => {
        if (!canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        setIsDragging(true);
        setDragStart({ x, y });
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;

        // Update image position when dragging
        setImagePosition(prev => ({
            x: prev.x + deltaX,
            y: prev.y + deltaY
        }));

        setDragStart({ x, y });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleTouchStart = (e) => {
        if (!canvasRef.current || e.touches.length !== 1) return;

        const touch = e.touches[0];
        const rect = canvasRef.current.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;

        setIsDragging(true);
        setDragStart({ x, y });
    };

    const handleTouchMove = (e) => {
        if (!isDragging || !canvasRef.current || e.touches.length !== 1) return;
        e.preventDefault();

        const touch = e.touches[0];
        const rect = canvasRef.current.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;

        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;

        setImagePosition(prev => ({
            x: prev.x + deltaX,
            y: prev.y + deltaY
        }));

        setDragStart({ x, y });
    };

    const handleSizeChange = (dimension, value) => {
        const newValue = parseFloat(value) / 100;

        if (dimension === 'width') {
            let newWidth = Math.max(0.1, Math.min(1, newValue));
            let newHeight = crop.height;

            if (aspectRatio) {
                newHeight = newWidth / aspectRatio * (imageObj.width / imageObj.height);
                newHeight = Math.min(1, newHeight);
                newWidth = newHeight * aspectRatio * (imageObj.height / imageObj.width);
            }

            // Adjust position if crop goes out of bounds
            const newX = Math.min(crop.x, 1 - newWidth);
            const newY = Math.min(crop.y, 1 - newHeight);

            setCrop({ x: newX, y: newY, width: newWidth, height: newHeight });
        } else {
            let newHeight = Math.max(0.1, Math.min(1, newValue));
            let newWidth = crop.width;

            if (aspectRatio) {
                newWidth = newHeight * aspectRatio * (imageObj.height / imageObj.width);
                newWidth = Math.min(1, newWidth);
                newHeight = newWidth / aspectRatio * (imageObj.width / imageObj.height);
            }

            const newX = Math.min(crop.x, 1 - newWidth);
            const newY = Math.min(crop.y, 1 - newHeight);

            setCrop({ x: newX, y: newY, width: newWidth, height: newHeight });
        }
    };

    const handleZoomChange = (value) => {
        const newZoom = parseFloat(value);
        const zoomDelta = newZoom / zoom;

        // Adjust image position to zoom towards center
        setImagePosition(prev => ({
            x: prev.x * zoomDelta,
            y: prev.y * zoomDelta
        }));

        setZoom(newZoom);
    };

    const handleCrop = () => {
        // Calculate the actual crop coordinates considering zoom and position
        if (!imageObj || !containerSize.width) {
            onCrop(crop);
            onClose();
            return;
        }

        const displayWidth = containerSize.width;
        const displayHeight = containerSize.height;
        const zoomedWidth = displayWidth * zoom;
        const zoomedHeight = displayHeight * zoom;

        // Crop coordinates in canvas space
        const cropX = crop.x * displayWidth;
        const cropY = crop.y * displayHeight;
        const cropWidth = crop.width * displayWidth;
        const cropHeight = crop.height * displayHeight;

        // Convert to image space considering zoom and position
        const imgX = (cropX - imagePosition.x) / zoom;
        const imgY = (cropY - imagePosition.y) / zoom;
        const imgWidth = cropWidth / zoom;
        const imgHeight = cropHeight / zoom;

        // Normalize to 0-1 range
        const normalizedCrop = {
            x: imgX / displayWidth,
            y: imgY / displayHeight,
            width: imgWidth / displayWidth,
            height: imgHeight / displayHeight
        };

        onCrop(normalizedCrop);
        onClose();
    };

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10001] p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-3xl shadow-elevated max-w-4xl w-full mx-4 h-[90vh] flex flex-col animate-scale-in"
                onClick={(e) => e.stopPropagation()}
                dir="rtl"
            >
                {/* Fixed Header */}
                <div className="sticky top-0 z-10 bg-white rounded-t-3xl px-6 py-5 border-b border-line-subtle flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-ink-strong font-heading">חיתוך ושינוי גודל תמונה</h2>
                    <button
                        onClick={onClose}
                        className="text-ink-subtle hover:text-ink-muted text-3xl w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-muted transition-all"
                    >
                        ×
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                    <div className="flex justify-center bg-surface-muted rounded-2xl p-4">
                        <canvas
                            ref={canvasRef}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleMouseUp}
                            className="max-w-full cursor-move touch-none rounded-xl shadow-lg"
                            style={{ touchAction: 'none' }}
                        />
                    </div>

                    <div className="space-y-4 bg-gradient-to-br from-surface-muted to-white p-5 rounded-2xl border border-line-subtle">
                        <p className="text-sm text-ink-muted font-medium">גרור את התמונה כדי להזיז אותה בתוך מסגרת החיתוך</p>

                        <div>
                            <label className="block text-sm font-semibold text-ink mb-2 flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                </svg>
                                זום: {zoom.toFixed(1)}x
                            </label>
                            <input
                                type="range"
                                min="1"
                                max="3"
                                step="0.1"
                                value={zoom}
                                onChange={(e) => handleZoomChange(e.target.value)}
                                className="w-full h-2 bg-surface-sunken rounded-lg appearance-none cursor-pointer accent-brand-500"
                            />
                            <div className="flex justify-between text-xs text-ink-subtle mt-1">
                                <span>1x</span>
                                <span>3x</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fixed Footer */}
                <div className="sticky bottom-0 z-10 bg-white rounded-b-3xl px-6 py-5 border-t border-line-subtle flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-2xl bg-surface-muted hover:bg-surface-sunken text-ink font-bold transition-all active:scale-95"
                    >
                        ביטול
                    </button>
                    <button
                        type="button"
                        onClick={handleCrop}
                        className="px-6 py-2.5 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 text-white hover:from-brand-600 hover:to-brand-700 transition-all active:scale-95 font-bold shadow-lg"
                    >
                        אישור חיתוך
                    </button>
                </div>
            </div>
        </div>
    );
};
